<script lang="ts">
	import Material from "../themes/Material.svelte";

	import InlineLocale from "./../lib/InlineLocale.svelte";

	import Row from "./Row.svelte";
	import Column from "./Column.svelte";
	import Editor from "./Editor.svelte";
	import ColumnsHeader from "./ColumnsHeader.svelte";
	import en from "../en";
	import { en as coreEn } from "@xbs/wx-core-locales";

	import type {
		ICard,
		IColumn,
		TEditorShape,
		IRow,
		THandlersConfig,
		TReadonlyConfig,
		TRenderType,
		TScrollType,
		TLayoutType,
		TCardShape,
		TColumnShape,
		TRowShape,
		IEditorConfig,
		TLocaleGroup,
	} from "@xbs/lib-kanban";

	import type { IEventBus } from "@xbs/lib-state";
	import { EventBusRouter } from "@xbs/lib-state";

	import { Locale } from "@xbs/svelte-wx";
	import {
		createEventDispatcher,
		getContext,
		onDestroy,
		onMount,
		tick,
	} from "svelte";
	import { writable } from "svelte/store";

	import {
		getApi,
		dndAction,
		selection,
		scroll,
		keyManager,
		defaultCardShape,
		DataStore,
		getAreaId,
		elementsIds,
		defaultEditorConfig,
	} from "@xbs/lib-kanban";
	import VirtualContent from "./VirtualContent.svelte";

	export let columns: IColumn[];
	export let rows: IRow[] = null;
	export let cards: ICard[];
	export let cardShape: TCardShape = defaultCardShape;
	export let columnShape: TColumnShape = null;
	export let rowShape: TRowShape = null;
	export let editorShape: TEditorShape[] = null;
	export let readonly: TReadonlyConfig = false;
	export let columnKey = "column";
	export let rowKey = "";
	export let scrollType: TScrollType = "default";
	export let renderType: TRenderType = "default";
	export let cardHeight: number | null = null;
	export let cardTemplate = null;
	export let editor: IEditorConfig = defaultEditorConfig;
	export let history = true;

	/** @deprecated use editor.autoSave instead */
	export let editorAutoSave = true;
	$: {
		if (editorAutoSave === false) {
			editor.autoSave = false;
		}
	}

	const dispatch = createEventDispatcher();
	// [TODO] maybe, find more elegant way
	onMount(() => {
		if (!document.querySelector(".wx-portal")) {
			const portal = document.createElement("div");
			portal.classList.add("wx-portal");
			portal.classList.add("wx-material-theme");
			document.body.appendChild(portal);
		}
	});
	onDestroy(() => {
		document.querySelector(".wx-portal")?.remove();
	});

	const dataStore = new DataStore(x => writable(x), { history });

	let lastInRoute: IEventBus<THandlersConfig> = new EventBusRouter(
		dispatch as any
	);

	dataStore.out.setNext(lastInRoute.exec);
	export const api = getApi(dataStore, lastInRoute);

	const handleAction = ({ detail: { action, data } }) => {
		switch (action) {
			case "open-card-menu":
				if (api.getState().selected?.length > 1) {
					api.exec("select-card", { id: data.id });
				}
				break;
			default:
				api.exec(action, data);
		}
	};

	let {
		columns: storeColumns,
		rowKey: storeRowKey,
		rows: storeRows,
		readonly: storeReadonly,
		dragItemId: movedCardId,
		areasMeta,
		cardsMap,
	} = dataStore.getReactive();

	$: {
		dataStore.init({
			columnKey,
			rowKey,
			columns,
			rows,
			cards,
			cardsMap: {},
			cardsMeta: {},
			cardShape,
			columnShape,
			rowShape,
			editorShape,
			readonly,
			layout,
			cardHeight,
		});
	}

	$: ({ edit, select, dnd } = $storeReadonly);

	let contentEl: HTMLElement;

	$: layout = `${scrollType}:${renderType}` as TLayoutType;

	let _: TLocaleGroup;

</script>

<Locale words={{ ...coreEn, ...en }} optional>
	<Material>
		<InlineLocale bind:_ />
		{#if !!_}
			<div
				class="wx-kanban"
				class:wx-dragged={!!$movedCardId}
				data-wx-widget={elementsIds.kanban}
				use:dndAction={{ api, readonly: dnd === false }}
				use:keyManager={{ api, readonly: edit === false, locale: _ }}
				use:scroll={{ api, tick }}>
				<div
					class="wx-content-wrapper"
					class:wx-virtual-content={layout === 'default:lazy'}
					data-kanban-id={elementsIds.content}>
					<div
						class="wx-content"
						class:wx-virtual-content={layout === 'default:lazy'}
						data-kanban-id={elementsIds.scrollableContent}
						bind:this={contentEl}
						use:selection={{ api, readonly: select === false }}>
						{#if layout === 'default:lazy'}
							<VirtualContent
								store={dataStore}
								let:startIndex
								let:endIndex
								let:byRow>
								<ColumnsHeader
									columns={$storeColumns}
									areasMeta={$areasMeta}
									on:action={handleAction}
									{contentEl}
									store={dataStore} />
								{#if $storeColumns.length}
									{#each $storeRows as row (row.id)}
										{#if byRow[row.id].visible}
											<Row
												{row}
												rows={$storeRows}
												collapsable={!!$storeRowKey}
												on:action={handleAction}
												{contentEl}
												store={dataStore}>
												{#each $storeColumns as column (column.id)}
													<Column
														on:action={handleAction}
														store={dataStore}
														{column}
														{row}
														areaMeta={$areasMeta[getAreaId(column.id, row.id)]}
														cards={byRow[row.id] ? $cardsMap[getAreaId(column.id, row.id)]?.slice(byRow[row.id].startIndex, byRow[row.id].endIndex) : $cardsMap[getAreaId(column.id, row.id)]?.slice(startIndex, endIndex)}
														{cardTemplate} />
												{/each}
											</Row>
										{/if}
									{/each}
								{/if}
							</VirtualContent>
						{:else}
							<ColumnsHeader
								columns={$storeColumns}
								areasMeta={$areasMeta}
								on:action={handleAction}
								{contentEl}
								store={dataStore} />
							{#if $storeColumns.length}
								{#each $storeRows as row (row.id)}
									<Row
										{row}
										rows={$storeRows}
										collapsable={!!$storeRowKey}
										on:action={handleAction}
										{contentEl}
										store={dataStore}>
										{#each $storeColumns as column (column.id)}
											<Column
												on:action={handleAction}
												store={dataStore}
												{column}
												{row}
												areaMeta={$areasMeta[getAreaId(column.id, row.id)]}
												cards={$cardsMap[getAreaId(column.id, row.id)]}
												{cardTemplate} />
										{/each}
									</Row>
								{/each}
							{/if}
						{/if}
					</div>
				</div>
				{#if edit && !$movedCardId}
					<Editor {api} config={editor} />
				{/if}
			</div>
		{/if}
	</Material>
</Locale>

<style>
	.wx-kanban * {
		box-sizing: border-box;
		font-family: var(--wx-font);
	}
	.wx-kanban {
		display: flex;
		flex-direction: row;
		position: relative;

		width: 100%;
		height: 100%;
		max-width: 100%;
		max-height: 100%;

		background: var(--wx-kanban-background);
		color: var(--wx-color-font);
		overflow: hidden;

		z-index: var(--wx-kanban-z-index);
	}
	.wx-dragged * {
		user-select: none;
	}
	.wx-dragged .wx-content-wrapper {
		overflow: hidden;
	}
	.wx-content-wrapper {
		display: flex;
		flex: 1 1 auto;
		overflow: auto;
		position: relative;
	}
	.wx-content {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		flex: 1 1 auto;

		position: relative;
		height: max-content;
		padding-bottom: var(--wx-padding);
	}
	.wx-virtual-content {
		height: 100%;
		max-width: 100%;
		overflow: hidden;
		padding-bottom: 0;
	}
	:global(.wx-portal .wx-dragged-card) {
		font-family: Roboto, sans-serif;
		color: var(--wx-color-font);
		width: var(--wx-kanban-column-width);
		min-height: 70px;
		position: fixed;
		z-index: 999999;
		user-select: none;
		pointer-events: none;

		box-shadow: 0 19px 38px rgba(0, 0, 0, 0.3),
			0 15px 12px rgba(0, 0, 0, 0.22);
	}
	:global(.wx-portal .wx-dragged-card:after) {
		content: var(--wx-kanban-dragged-cards-count);
		display: flex;
		align-items: center;
		justify-content: center;
		width: 30px;
		height: 30px;
		background: var(--wx-color-primary);
		color: var(--wx-color-primary-font);
		position: absolute;
		border-radius: 50%;
		top: -10px;
		right: -10px;
		z-index: 1;
	}
	:global(.wx-ondrag) {
		overflow: hidden;
	}

</style>
